import { config } from "./config";

export class ApiError extends Error {
  constructor(public status: number, message: string, public retryAfterSeconds?: number) {
    super(message);
    this.name = "ApiError";
  }
}

export interface WorkspaceMember {
  userId: string;
  email: string | null;
  displayName: string | null;
  role: string;
}

export interface WorkspaceSummary {
  id: string;
  name: string;
  createdByUserId: string | null;
  members: WorkspaceMember[];
}

export interface DemoCopierResult {
  jobName: string;
  workspaceId: string;
  projectOwnerId: string;
  sampleProjectId: string;
}

export interface AdminUser {
  userId: string;
  email: string | null;
  displayName: string | null;
}

export interface RoleMember extends AdminUser {
  roles: string[]; // subset of ["drawboard-admin", "projects-devs", "cloud-team"]
}

export interface MeResponse extends AdminUser {
  roles: string[];
}

export interface WorkloadActionResult {
  kind: string;
  name: string;
  ok: boolean;
  error: string | null;
}

export interface UserSearchResult {
  userId: string;
  email: string | null;
  displayName: string | null;
}

// --- Support: user detail panel (Bullclip + Identity sides, merged client-side) ---

/** Bullclip-side user detail (DBCloud.WebAPI /api/admin/users/{id}). */
export interface AdminUserDetail {
  userId: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  email: string | null;
  persona: string; // "StandardUser" | "LinkShareUser" | "ApiKeyUser" | "Undefined"
  isActive: boolean;
  lastActive: string | null;
  hasOrganization: boolean;
  organizationId: string | null;
  organizationName: string | null;
  role: string | null; // "Administrator" | "Member" when in an org
  /** Total members in their organization (null when they have none). 1 means they are its only
   *  member, which is what lets an administrator be removed or deleted. */
  organizationMemberCount: number | null;
}

export interface ExternalLogin {
  loginProvider: string;
  displayName: string;
}

/** Identity-side user detail (identity server /api/admin/users/{id}/identity). */
export interface IdentityUserDetail {
  userId: string;
  email: string | null;
  emailConfirmed: boolean;
  hasPassword: boolean;
  isLockedOut: boolean;
  lockoutEnd: string | null;
  accessFailedCount: number;
  providers: ExternalLogin[];
}

export interface UserNameRow {
  userId: string;
  displayName: string | null;
  // false => identity record exists (search returned it) but no Bullclip profile: an orphaned/broken user.
  hasProfile: boolean;
}

// --- Support: organizations ---
export interface OrgListItem {
  id: string;
  name: string;
  createdDateTimeUtc: string;
  memberCount: number;
}

export interface OrgListPage {
  items: OrgListItem[];
  total: number;
  skip: number;
  take: number;
}

export interface OrgEntitlement {
  name: string;
  value: unknown; // boolean | number | string
  kind: string; // "BooleanFlag" | "LongLimit" | "StringValue" | "Unknown"
}

/** An entitlement the application understands — for the add-entitlement picker. */
export interface KnownEntitlement {
  id: string;
  kind: string; // "BooleanFlag" | "LongLimit"
}

export interface OrgSubscription {
  hasSubscription: boolean;
  purchasedSeats: number | null;
  usedSeats: number | null;
  planId: string | null;
  status: string | null;
  currentTermEnd: string | null;
  entitlements: OrgEntitlement[];
  chargebeeCustomerUrl: string | null;
  chargebeeSubscriptionUrl: string | null;
  verifiedDomain: string | null;
  subscriptionId: string | null;
}

/** Preview of a candidate ChargeBee subscription for the change-subscription dialog. `belongsToCustomer`
 *  is true only when the subscription is owned by the same ChargeBee customer as the org. */
export interface SubscriptionPreview {
  exists: boolean;
  belongsToCustomer: boolean;
  currencyCode: string | null;
  planId: string | null;
  quantity: number | null;
  status: string | null;
}

export type InviteStatus = "pending" | "accepted" | "expired";

export interface OrgInvite {
  id: string;
  recipientEmail: string | null;
  organizationId: string;
  organizationName: string | null;
  role: string | null;
  status: InviteStatus;
  createdDateTimeUtc: string;
  expiryDateUtc: string;
}

export interface GuestLink {
  id: string;
  recipientEmail: string | null;
  role: string | null;
  status: InviteStatus;
  projectId: string;
  projectName: string | null;
  workspaceId: string | null;
  workspaceName: string | null;
  organizationId: string | null;
  organizationName: string | null;
  createdDateUtc: string;
  expiryDateUtc: string;
}

export interface OrgDetail {
  id: string;
  name: string;
  createdDateTimeUtc: string;
  memberCount: number;
  subscription: OrgSubscription;
}

/** One email's outcome in a bulk invite. */
export interface BulkInviteRow {
  email: string;
  status: string; // "invited" | "skipped" | "error"
  inviteId: string | null;
  error: string | null;
}

/** Outcome of the optional verified-domain set in a bulk invite. */
export interface BulkInviteDomainResult {
  domain: string;
  status: string; // "set" | "error"
  error: string | null;
}

export interface BulkInviteResult {
  results: BulkInviteRow[];
  domain: BulkInviteDomainResult | null;
}

export interface OrgMember extends UserSearchResult {
  role: string | null;
  isActive: boolean;
}

/** The two organization roles, spelled as the backend's OrganizationRole enum names. */
export type OrgRole = "Administrator" | "Member";

/** Result of a sales-assisted create-organization: the new org-admin user + everything brought up for them. */
export interface CreateOrgResult {
  userId: string;
  organizationId: string;
  workspaceId: string | null;
  organizationName: string;
}

/** A user id + email, returned by both backends' /users/ids for orphan reconciliation. */
export interface UserIdRow {
  userId: string;
  email: string | null;
}

// --- Support: deleted-document restore (DBCloud.WebAPI /api/admin/projects/{id}/...) ---
export interface DeletedDocument {
  documentId: string;
  name: string;
  type: string; // "Drawing" | "Document"
  sheetId: string | null;
  latestRevisionNumber: number;
  deletedOnUtc: string | null;
  createdUtc: string;
}

// --- Observability DTOs (mirror DBCloud.AdminPortalApi) ---
export interface WorkloadContainer {
  name: string;
  image: string;
}
export interface Workload {
  kind: "Deployment" | "StatefulSet";
  name: string;
  desiredReplicas: number;
  readyReplicas: number;
  availableReplicas: number;
  updatedReplicas: number;
  containers: WorkloadContainer[];
  selector: Record<string, string> | null;
}
export interface IngressPath {
  path: string;
  serviceName: string;
  servicePort: number | null;
}
export interface IngressRule {
  host: string;
  paths: IngressPath[];
}
export interface Ingress {
  name: string;
  ingressClassName: string | null;
  loadBalancerIp: string | null;
  tlsSecretNames: string[];
  rules: IngressRule[];
}
export interface Certificate {
  name: string;
  secretName: string | null;
  dnsNames: string[];
  notAfter: string | null;
  renewalTime: string | null;
  ready: string | null;
  readyReason: string | null;
  readyMessage: string | null;
}
export interface K8sEvent {
  type: string;
  reason: string;
  message: string;
  count: number;
  firstTimestamp: string | null;
  lastTimestamp: string | null;
  involvedObjectKind: string | null;
  involvedObjectName: string | null;
  source: string | null;
}
export interface PodSummary {
  name: string;
  phase: string;
  nodeName: string | null;
  startTime: string | null;
  restartCount: number;
  ready: boolean;
  containerImages: string[];
}
export interface PodContainerStatus {
  state: string;
  reason: string | null;
  ready: boolean;
  restartCount: number;
  startedAt: string | null;
}
export interface PodEnv {
  name: string;
  value: string | null;
  valueFrom: string | null;
}
export interface PodContainer {
  name: string;
  image: string;
  env: PodEnv[];
  status: PodContainerStatus | null;
}
export interface PodDetail {
  name: string;
  namespace: string;
  phase: string;
  nodeName: string | null;
  podIp: string | null;
  startTime: string | null;
  labels: Record<string, string>;
  containers: PodContainer[];
}

// --- Cost DTOs (mirror DBCloud.AdminPortalApi CostController) ---
export interface AllocationItem {
  name: string;
  cpuCost: number;
  ramCost: number;
  pvCost: number;
  networkCost: number;
  loadBalancerCost: number;
  gpuCost: number;
  sharedCost: number;
  totalCost: number;
  windowStart: string | null;
  windowEnd: string | null;
}

export interface AllocationResponse {
  items: AllocationItem[];
  warmingUp: boolean;
  message: string | null;
  namespace: string;
}

export interface CostRow {
  name: string;
  cost: number;
  currency: string | null;
}

export interface ResourcesResponse {
  resourceGroup: string;
  timeframe: string;
  dimension: string;
  currency: string | null;
  total: number;
  rows: CostRow[];
}

export interface MasterProjectStatus {
  statefulSet: string;
  envName: string;
  liveValue: string | null;
}

export interface MasterProjectReleaseResult {
  jobName: string;
  sourceProjectId: string;
  workspaceId: string | null;
  newProjectId: string;
}

// --- Scheduled outages (DBCloud.AdminPortalApi: /api/outages) ---
export type OutageState = "scheduled" | "in_progress" | "restored" | "cancelled";

export interface OutageTakedown {
  state: "taking_down" | "down" | "restoring" | "restored";
  stepsTotal: number;
  stepsDone: number;
  stepsRestored: number;
  /** Short human string for the step being worked on, e.g. "clients: projects-webapi". */
  current: string | null;
  /**
   * Restore-the-silo: "requested" once pressed and until every silo step is back, "restored" once the silo is Ready at its
   * recorded count (whether restore-the-silo or Restore brought it back), else "none".
   */
  siloRestore: "none" | "requested" | "restored";
}

export interface Outage {
  id: string;
  state: OutageState;
  /** The Sydney calendar date the outage was booked for (null when it was booked by explicit start). */
  date: string | null;
  durationMinutes: number;
  /** UTC instants, ISO-8601 with a trailing Z. */
  start: string;
  end: string;
  message: string;
  enabled: boolean;
  automateTakedown: boolean;
  revision: number;
  /** Absent or null when no automated takedown is attached to this outage. */
  takedown?: OutageTakedown | null;
}

/** A booking or an edit: a Sydney date and a duration. The server owns the timeslot rule, so no start is ever sent. */
export interface OutageBooking {
  /** Exactly one of date and start is sent. An edit that keeps the time of an explicit-start outage sends its start. */
  date?: string;
  start?: string;
  durationMinutes: number;
  message: string;
  automateTakedown: boolean;
  enabled?: boolean;
}

export type OutageAction = "cancel" | "enable" | "disable";

type TokenGetter = () => string | undefined;

async function request<T>(baseUrl: string, path: string, token: string | undefined, init?: RequestInit): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data = text ? safeJson(text) : undefined;

  if (!res.ok) {
    const detail =
      (data && (data.message || data.error || data.title || data.detail)) ||
      text ||
      `${res.status} ${res.statusText}`;
    // 429s carry the advised wait in the body (the Retry-After header isn't CORS-exposed).
    const retryAfterSeconds = typeof data?.retryAfterSeconds === "number" ? data.retryAfterSeconds : undefined;
    throw new ApiError(res.status, detail, retryAfterSeconds);
  }
  return data as T;
}

async function requestText(baseUrl: string, path: string, token: string | undefined): Promise<string> {
  const res = await fetch(`${baseUrl}${path}`, {
    headers: {
      Accept: "text/plain",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const text = await res.text();
  if (!res.ok) {
    throw new ApiError(res.status, text || `${res.status} ${res.statusText}`);
  }
  return text;
}

function safeJson(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

export function createApi(getToken: TokenGetter) {
  const api = config.apiBaseUrl;
  const sso = config.authority;
  const adminApi = config.adminApiBaseUrl;
  return {
    // --- DBCloud.WebAPI: /api/admin/* (gated by the projects-admin scope) ---
    getWorkspace: (workspaceId: string) =>
      request<WorkspaceSummary>(api, `/api/admin/workspaces/${encodeURIComponent(workspaceId)}`, getToken()),

    runDemoProjectCopier: (body: { workspaceId: string; projectOwnerId: string; sampleProjectId?: string }) =>
      request<DemoCopierResult>(api, `/api/admin/demo-project-copier/run`, getToken(), {
        method: "POST",
        body: JSON.stringify(body),
      }),

    /** The workspace a project lives in — used to default a master release into the source's own workspace. */
    getProjectWorkspace: (projectId: string) =>
      request<{ projectId: string; workspaceId: string }>(api, `/api/admin/projects/${encodeURIComponent(projectId)}/workspace`, getToken()),

    /** Soft-deleted documents/drawings in a project — the recovery list. */
    listDeletedDocuments: (projectId: string) =>
      request<DeletedDocument[]>(api, `/api/admin/projects/${encodeURIComponent(projectId)}/deleted-documents`, getToken()),

    /** Restore one soft-deleted document/drawing. Idempotent server-side. */
    restoreDocument: (projectId: string, documentId: string) =>
      request<void>(api, `/api/admin/projects/${encodeURIComponent(projectId)}/documents/${encodeURIComponent(documentId)}/restore`, getToken(), {
        method: "POST",
      }),

    // --- DBCloud.AdminPortalApi: scheduled outages ---
    listOutages: () =>
      request<Outage[]>(adminApi, `/api/outages`, getToken()),

    /** The text the booking form prefills. Served from admin-portal-api configuration. */
    getOutageDefaultMessage: async (): Promise<string> => {
      const r = await request<string | { message?: string }>(adminApi, `/api/outages/default-message`, getToken());
      return typeof r === "string" ? r : (r?.message ?? "");
    },

    bookOutage: (body: OutageBooking) =>
      request<Outage>(adminApi, `/api/outages`, getToken(), { method: "POST", body: JSON.stringify(body) }),

    updateOutage: (id: string, body: OutageBooking) =>
      request<Outage>(adminApi, `/api/outages/${encodeURIComponent(id)}`, getToken(), { method: "PUT", body: JSON.stringify(body) }),

    outageAction: (id: string, action: OutageAction) =>
      request<Outage>(adminApi, `/api/outages/${encodeURIComponent(id)}/${action}`, getToken(), { method: "POST" }),

    /** Cloud-team only. 202 started or already restoring, 200 already restored; 404, 409 (scheduled or cancelled), 403. */
    restoreOutage: (id: string) =>
      request<Outage>(adminApi, `/api/outages/${encodeURIComponent(id)}/restore`, getToken(), { method: "POST" }),

    /**
     * Cloud-team only. Brings the silo alone back while the takedown stays down and the outage stays in progress.
     * 202 recorded or already pending; 200 no change (the silo is already back, or Restore has begun or finished);
     * 409 no takedown serves the outage; 404 unknown; 403.
     */
    restoreSilo: (id: string) =>
      request<Outage>(adminApi, `/api/outages/${encodeURIComponent(id)}/restore-silo`, getToken(), { method: "POST" }),

    // --- DBCloud.AdminPortalApi: master sample-project verbatim copy ---
    getCurrentMasterProject: () =>
      request<MasterProjectStatus>(adminApi, `/api/admin/master-project/current`, getToken()),

    releaseMasterProject: (body: { sampleProjectId: string; workspaceId: string; newProjectId?: string }) =>
      request<MasterProjectReleaseResult>(adminApi, `/api/admin/master-project/release`, getToken(), {
        method: "POST",
        body: JSON.stringify(body),
      }),

    // --- DBCloud.IdentityServer: /api/admin/role-members (all three portal roles) ---
    listRoleMembers: () => request<RoleMember[]>(sso, `/api/admin/role-members`, getToken()),

    getMe: () => request<MeResponse>(sso, `/api/admin/me`, getToken()),

    /** Add a new role-member by email. Roles must include drawboard-admin (baseline). */
    addRoleMember: (email: string, roles: string[]) =>
      request<RoleMember>(sso, `/api/admin/role-members`, getToken(), {
        method: "POST",
        body: JSON.stringify({ email, roles }),
      }),

    /** Replace the user's portal-role set. Pass [] to remove from the portal entirely. */
    setRoleMembership: (userId: string, roles: string[]) =>
      request<RoleMember>(sso, `/api/admin/role-members/${encodeURIComponent(userId)}`, getToken(), {
        method: "PUT",
        body: JSON.stringify({ roles }),
      }),

    // --- Support: user search + admin-driven password reset ---
    searchUsers: (q: string) =>
      request<UserSearchResult[]>(sso, `/api/admin/users/search?q=${encodeURIComponent(q)}`, getToken()),

    resetUserPassword: (userId: string, password: string) =>
      request<AdminUser>(sso, `/api/admin/users/${encodeURIComponent(userId)}/reset-password`, getToken(), {
        method: "POST",
        body: JSON.stringify({ password }),
      }),

    // --- Support: user detail panel ---
    // Bullclip side (DBCloud.WebAPI): profile, org + role, active flag, last active.
    getBullclipUser: (userId: string) =>
      request<AdminUserDetail>(api, `/api/admin/users/${encodeURIComponent(userId)}`, getToken()),

    // Batch display-name enrichment for the search list (real names live in Bullclip, not identity).
    getUserNames: (userIds: string[]) =>
      request<UserNameRow[]>(api, `/api/admin/users/names`, getToken(), {
        method: "POST",
        body: JSON.stringify({ userIds }),
      }),

    // Enable/disable in Bullclip — runs the billing-aware flow server-side (seat re-sync on enable).
    setUserActive: (userId: string, active: boolean) =>
      request<AdminUserDetail>(api, `/api/admin/users/${encodeURIComponent(userId)}/active`, getToken(), {
        method: "POST",
        body: JSON.stringify({ active }),
      }),

    // Change the user's role in their own organization ("Administrator" | "Member"). Rejected when it
    // would leave the organization with no administrator.
    setUserOrgRole: (userId: string, role: OrgRole) =>
      request<AdminUserDetail>(api, `/api/admin/users/${encodeURIComponent(userId)}/role`, getToken(), {
        method: "POST",
        body: JSON.stringify({ role }),
      }),

    // Change a user's email immediately (no verification), across Bullclip + the identity server.
    // Sensitive override — the panel warns before calling, loudly for SSO/federated users.
    changeUserEmail: (userId: string, email: string) =>
      request<AdminUserDetail>(api, `/api/admin/users/${encodeURIComponent(userId)}/email`, getToken(), {
        method: "POST",
        body: JSON.stringify({ email }),
      }),

    // Fully remove from org (distinct from disable): deletes membership + all project/workspace access,
    // converts to a share-link (guest) user, and locks sign-in until re-invited.
    removeUserFromOrganization: (userId: string) =>
      request<AdminUserDetail>(api, `/api/admin/users/${encodeURIComponent(userId)}/remove-from-org`, getToken(), {
        method: "POST",
      }),

    // Permanently delete a user (hard delete: identity + profile + all access). Owned projects and
    // sole-admined workspaces are handed to an org admin first. Returns 204.
    deleteUser: (userId: string) =>
      request<void>(api, `/api/admin/users/${encodeURIComponent(userId)}/delete`, getToken(), {
        method: "POST",
      }),

    // Identity side: email verification, lockout, password-vs-SSO, login providers.
    getIdentityUser: (userId: string) =>
      request<IdentityUserDetail>(sso, `/api/admin/users/${encodeURIComponent(userId)}/identity`, getToken()),

    forceVerifyEmail: (userId: string) =>
      request<IdentityUserDetail>(sso, `/api/admin/users/${encodeURIComponent(userId)}/verify-email`, getToken(), {
        method: "POST",
      }),

    unlockUser: (userId: string) =>
      request<IdentityUserDetail>(sso, `/api/admin/users/${encodeURIComponent(userId)}/unlock`, getToken(), {
        method: "POST",
      }),

    // --- Support: organizations (DBCloud.WebAPI) ---
    listOrganizations: (opts?: { filter?: string; skip?: number; take?: number }) => {
      const params = new URLSearchParams();
      if (opts?.filter) params.set("filter", opts.filter);
      params.set("skip", String(opts?.skip ?? 0));
      params.set("take", String(opts?.take ?? 25));
      return request<OrgListPage>(api, `/api/admin/organizations?${params.toString()}`, getToken());
    },

    getOrganization: (orgId: string) =>
      request<OrgDetail>(api, `/api/admin/organizations/${encodeURIComponent(orgId)}`, getToken()),

    /** Sales-assisted signup: create an org whose first Administrator is the given person. Runs the
     *  full self-registration flow server-side (identity user, profile, org, workspace, ChargeBee).
     *  The password is generated in the portal and shown once for hand-off to the customer. */
    createOrganizationForCustomer: (body: {
      organizationName: string;
      email: string;
      firstName: string;
      lastName: string;
      password: string;
      createDemoData: boolean;
    }) =>
      request<CreateOrgResult>(api, `/api/admin/organizations`, getToken(), {
        method: "POST",
        body: JSON.stringify(body),
      }),

    // --- Entitlement administration (ChargeBee entitlement_overrides) ---
    getKnownEntitlements: () =>
      request<KnownEntitlement[]>(api, `/api/admin/entitlements/known`, getToken()),

    /** Upsert an entitlement override: adds the feature or overrides its value.
     *  Value: "true"/"false" for switches, a number or "unlimited" for limits. */
    setOrgEntitlement: (orgId: string, featureId: string, value: string) =>
      request<void>(api, `/api/admin/organizations/${encodeURIComponent(orgId)}/entitlements/${encodeURIComponent(featureId)}`, getToken(), {
        method: "PUT",
        body: JSON.stringify({ value }),
      }),

    /** Remove an entitlement override — the feature falls back to the plan's default. */
    removeOrgEntitlement: (orgId: string, featureId: string) =>
      request<void>(api, `/api/admin/organizations/${encodeURIComponent(orgId)}/entitlements/${encodeURIComponent(featureId)}`, getToken(), {
        method: "DELETE",
      }),

    getOrgMembers: (orgId: string) =>
      request<OrgMember[]>(api, `/api/admin/organizations/${encodeURIComponent(orgId)}/members`, getToken()),

    /** Bulk-invite emails to an org (each attributed to the org's existing admin) and, optionally, set
     *  the org's ChargeBee verified domain — which blocks that email domain from self-registering. */
    bulkInviteToOrganization: (orgId: string, body: { emails: string[]; role: string; domain?: string }) =>
      request<BulkInviteResult>(api, `/api/admin/organizations/${encodeURIComponent(orgId)}/bulk-invite`, getToken(), {
        method: "POST",
        body: JSON.stringify(body),
      }),

    /** Set — or, with a blank string, clear — the org's ChargeBee verified domain. */
    setOrganizationVerifiedDomain: (orgId: string, domain: string) =>
      request<void>(api, `/api/admin/organizations/${encodeURIComponent(orgId)}/verified-domain`, getToken(), {
        method: "PUT",
        body: JSON.stringify({ domain }),
      }),

    /** Preview a candidate ChargeBee subscription before re-pointing the org at it (read-only). */
    previewOrganizationSubscription: (orgId: string, subscriptionId: string) =>
      request<SubscriptionPreview>(
        api,
        `/api/admin/organizations/${encodeURIComponent(orgId)}/subscription-preview?subscriptionId=${encodeURIComponent(subscriptionId)}`,
        getToken(),
      ),

    /** Re-point the org at a different ChargeBee subscription (must belong to the org's ChargeBee customer). */
    setOrganizationSubscriptionId: (orgId: string, subscriptionId: string) =>
      request<void>(api, `/api/admin/organizations/${encodeURIComponent(orgId)}/subscription-id`, getToken(), {
        method: "PUT",
        body: JSON.stringify({ subscriptionId }),
      }),

    // --- Support: broken-user reconciliation (Bullclip vs identity id sets) ---
    getBullclipUserIds: () => request<UserIdRow[]>(api, `/api/admin/users/ids`, getToken()),
    getIdentityUserIds: () => request<UserIdRow[]>(sso, `/api/admin/users/ids`, getToken()),

    /** Of the given ids, which exist as identity-server users — used to flag org members with no identity. */
    identityUsersExist: (userIds: string[]) =>
      request<string[]>(sso, `/api/admin/users/exist`, getToken(), {
        method: "POST",
        body: JSON.stringify({ userIds }),
      }),

    // --- Support: organization invitations + guest links (search + reissue) ---
    /** Without an email, the backend returns the latest 10 by created date. */
    searchOrganizationInvites: (email?: string) =>
      request<OrgInvite[]>(api, `/api/admin/organization-invites${email ? `?email=${encodeURIComponent(email)}` : ""}`, getToken()),

    /** Reissue an org invitation email. Does not change the expiry timeline. */
    resendOrganizationInvite: (id: string) =>
      request<void>(api, `/api/admin/organization-invites/${encodeURIComponent(id)}/resend`, getToken(), {
        method: "POST",
      }),

    /** Delete (revoke) an org invitation. The invite link stops working; the recipient isn't notified. */
    deleteOrganizationInvite: (id: string) =>
      request<void>(api, `/api/admin/organization-invites/${encodeURIComponent(id)}`, getToken(), {
        method: "DELETE",
      }),

    /** Without an email, the backend returns the latest 10 by created date. */
    searchShareLinks: (email?: string) =>
      request<GuestLink[]>(api, `/api/admin/sharelinks${email ? `?email=${encodeURIComponent(email)}` : ""}`, getToken()),

    /** Reissue a guest-link email. Does not change the expiry timeline. */
    resendShareLink: (id: string) =>
      request<void>(api, `/api/admin/sharelinks/${encodeURIComponent(id)}/resend`, getToken(), {
        method: "POST",
      }),

    // --- DBCloud.AdminPortalApi: cluster observability ---
    // scope = "self" (default; workspace namespace) | "parent" (env namespace — ingress-nginx, redis, redpanda)
    listWorkloads: (scope: "self" | "parent" = "self") =>
      request<Workload[]>(adminApi, `/api/observability/workloads?scope=${scope}`, getToken()),
    listIngresses: (scope: "self" | "parent" = "self") =>
      request<Ingress[]>(adminApi, `/api/observability/ingresses?scope=${scope}`, getToken()),
    listCertificates: (scope: "self" | "parent" = "self") =>
      request<Certificate[]>(adminApi, `/api/observability/certificates?scope=${scope}`, getToken()),
    listEvents: (involvedKind?: string, involvedName?: string, scope: "self" | "parent" = "self") => {
      const params = new URLSearchParams();
      if (involvedKind) params.set("involvedObjectKind", involvedKind);
      if (involvedName) params.set("involvedObjectName", involvedName);
      params.set("scope", scope);
      return request<K8sEvent[]>(adminApi, `/api/observability/events?${params.toString()}`, getToken());
    },
    listPods: (kind: string, name: string, scope: "self" | "parent" = "self") =>
      request<PodSummary[]>(adminApi, `/api/observability/pods?kind=${encodeURIComponent(kind)}&name=${encodeURIComponent(name)}&scope=${scope}`, getToken()),
    getPod: (name: string, scope: "self" | "parent" = "self") =>
      request<PodDetail>(adminApi, `/api/observability/pods/${encodeURIComponent(name)}?scope=${scope}`, getToken()),
    getPodLogs: (name: string, opts?: { container?: string; tailLines?: number; sinceSeconds?: number; previous?: boolean; scope?: "self" | "parent" }) => {
      const params = new URLSearchParams();
      if (opts?.container) params.set("container", opts.container);
      if (opts?.sinceSeconds) params.set("sinceSeconds", String(opts.sinceSeconds));
      else if (opts?.tailLines) params.set("tailLines", String(opts.tailLines));
      if (opts?.previous) params.set("previous", "true");
      params.set("scope", opts?.scope ?? "self");
      return requestText(adminApi, `/api/observability/pods/${encodeURIComponent(name)}/logs?${params.toString()}`, getToken());
    },

    // --- DBCloud.AdminPortalApi: cluster cost (OpenCost) ---
    // scope = "self" → workspace namespace, "parent" → env namespace (shared infra)
    getAllocation: (window: string, aggregate: string, scope: "self" | "parent" = "self") =>
      request<AllocationResponse>(
        adminApi,
        `/api/cost/allocation?window=${encodeURIComponent(window)}&aggregate=${encodeURIComponent(aggregate)}&scope=${scope}`,
        getToken()
      ),
    getResourceCost: (timeframe: string, dimension: string) =>
      request<ResourcesResponse>(
        adminApi,
        `/api/cost/resources?timeframe=${encodeURIComponent(timeframe)}&dimension=${encodeURIComponent(dimension)}`,
        getToken()
      ),

    // --- Workload control (cloud-team) ---
    restartWorkloads: (
      targets: { kind: string; name: string }[],
      scope: "self" | "parent" = "self",
    ) =>
      request<{ results: WorkloadActionResult[] }>(
        adminApi,
        `/api/observability/workloads/restart?scope=${scope}`,
        getToken(),
        { method: "POST", body: JSON.stringify({ targets }) },
      ),

    scaleWorkloads: (
      targets: { kind: string; name: string }[],
      replicas: number,
      scope: "self" | "parent" = "self",
    ) =>
      request<{ results: WorkloadActionResult[] }>(
        adminApi,
        `/api/observability/workloads/scale?scope=${scope}`,
        getToken(),
        { method: "POST", body: JSON.stringify({ targets, replicas }) },
      ),
  };
}

export type Api = ReturnType<typeof createApi>;
